/**
 * checkoutFacts — the read-only facts at the top of the Practice Ops Check-out panel.
 *
 * Five facts, in the design's order:
 *   1. Today's estimated patient portion   — the classic panel's own out-of-pocket query (kept, plan §4)
 *   2. Account balance                     — Schedule View's `balance` alert detail for this PatNum
 *   3. Recall                              — Schedule View's `recall` alert detail for this PatNum
 *   4. Planned treatment not scheduled     — the classic panel's planned-appointments query (kept)
 *   5. Open Teamio tasks                   — the tasks tagged with the patient, as the classic panel's Tasks list shows them
 *
 * WHY BALANCE AND RECALL COME FROM SCHEDULE VIEW (plan §4): the board's card icons and this panel must say the same thing
 * about the same patient, so both read the ONE alert payload (`/schedule-view/alerts` → `byPatNum[<PatNum>].<key>`), through
 * the same describer (`describeAlert`, utils/scheduleView/alerts.js). The panel's own recall / balance SQL results are not
 * read (they stay in the query only because the classic panel still reads the result sets BY POSITION).
 *
 * HONEST ABOUT WHAT IT DOES NOT KNOW. An alert that was not computed (`ran` lacks it: the office switched it off, the rule
 * failed, or the office cannot use Schedule View's alerts yet) is "Not available" — never "$0.00" or "Up to date". Only a rule
 * that RAN and found nothing says so.
 *
 * `deps` is injected so this file stays import-free (the node-tests execute it verbatim):
 *   { alertsForPatient(payload, patNum), describe(key, detail) } — both from utils/scheduleView/alerts.js.
 *
 * ⚠️ Keep this file import-free.
 */

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/** "$1,234.56" — two decimals (the card's tooltip rounds to whole dollars; a statement-style fact keeps the cents). */
export function formatMoneyExact(amount, symbol) {
  if (amount === null || amount === undefined || amount === '') return '';
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  const sign = n < 0 ? '-' : '';
  const fixed = Math.abs(n).toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  return `${sign}${symbol || '$'}${fixed}`;
}

/** The text of a task's HTML content (tags dropped, entities and runs of space collapsed). */
export function plainText(html) {
  if (typeof html !== 'string') return '';
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

function clip(text, max) {
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

const NOT_AVAILABLE = 'Not available';

/**
 * @param {object} input
 *   payload   the day's /alerts payload (the store's `scheduleView/alertsForDate`), or null while it has not loaded
 *   patNum    the patient
 *   oop       the classic panel's out-of-pocket rows (`patientOutofPocket`): [{ PatientOOP, ... }] | undefined while loading
 *   planned   the classic panel's planned appointments (`patientPlannedAppts`): [{ ProcDescript, ... }] | undefined while loading
 *   tasks     the tasks tagged with the patient: [{ content, status, deleted }] | undefined while loading
 *   symbol    the practice's currency symbol
 * @param {{alertsForPatient: Function, describe: Function}} deps
 * @returns {Array<{key: string, label: string, value: string, tone: 'ok'|'bad'|'muted', detail: string}>}
 */
export function buildCheckoutFacts(input, deps) {
  const i = isObj(input) ? input : {};
  const d = isObj(deps) ? deps : {};
  const symbol = i.symbol || '$';
  const ran = isObj(i.payload) && Array.isArray(i.payload.ran) ? i.payload.ran : [];
  const forPatient = isObj(i.payload) && typeof d.alertsForPatient === 'function' ? d.alertsForPatient(i.payload, i.patNum) : {};

  const facts = [];

  // 1. estimated patient portion (the classic query; `undefined` = still loading)
  {
    let value = '…';
    if (Array.isArray(i.oop)) {
      const row = i.oop[0];
      const amount = row && row.PatientOOP !== null && row.PatientOOP !== undefined ? Number(row.PatientOOP) : NaN;
      value = Number.isFinite(amount) ? formatMoneyExact(amount, symbol) : NOT_AVAILABLE;
    }
    facts.push({ key: 'portion', label: "Today's estimated patient portion", value, tone: value === NOT_AVAILABLE ? 'muted' : 'ok', detail: '' });
  }

  // 2. balance (Schedule View)
  {
    const fact = { key: 'balance', label: 'Account balance', value: NOT_AVAILABLE, tone: 'muted', detail: '' };
    if (ran.indexOf('balance') !== -1) {
      const detail = forPatient.balance;
      if (isObj(detail)) {
        fact.value = formatMoneyExact(detail.amount, symbol) || NOT_AVAILABLE;
        fact.tone = Number(detail.amount) > 0 ? 'bad' : 'ok';
        const who = isObj(detail.guarantor) && detail.guarantor.LName ? `${detail.guarantor.LName}'s account` : '';
        fact.detail = who ? `Family balance, on ${who}` : 'Family balance';
      } else {
        fact.value = formatMoneyExact(0, symbol);
        fact.tone = 'ok';
      }
    }
    facts.push(fact);
  }

  // 3. recall (Schedule View)
  {
    const fact = { key: 'recall', label: 'Recall', value: NOT_AVAILABLE, tone: 'muted', detail: '' };
    if (ran.indexOf('recall') !== -1) {
      const detail = forPatient.recall;
      if (isObj(detail)) {
        fact.value = typeof d.describe === 'function' ? d.describe('recall', detail) : 'Overdue for recall';
        fact.tone = 'bad';
      } else {
        fact.value = 'Up to date';
        fact.tone = 'ok';
      }
    }
    facts.push(fact);
  }

  // 4. planned treatment not scheduled (the classic query)
  {
    const fact = { key: 'planned', label: 'Planned treatment not scheduled', value: '…', tone: 'ok', detail: '' };
    if (Array.isArray(i.planned)) {
      if (i.planned.length === 0) {
        fact.value = 'None';
      } else {
        const first = i.planned[0] && typeof i.planned[0].ProcDescript === 'string' ? i.planned[0].ProcDescript.trim() : '';
        fact.value = `${i.planned.length} planned`;
        fact.detail = clip(first, 80);
      }
    }
    facts.push(fact);
  }

  // 5. open Teamio tasks tagged with this patient
  {
    const fact = { key: 'tasks', label: 'Open Teamio tasks', value: '…', tone: 'ok', detail: '' };
    if (Array.isArray(i.tasks)) {
      const open = i.tasks.filter((t) => isObj(t) && t.deleted !== true && (t.status === undefined || t.status === 'INCOMPLETE'));
      if (open.length === 0) {
        fact.value = 'None';
      } else {
        fact.value = String(open.length);
        fact.detail = clip(plainText(open[0].content), 80);
      }
    }
    facts.push(fact);
  }

  return facts;
}

/**
 * The visit notes after a quick-note chip is turned on or off — the classic panel's rule, written once:
 * ON appends the auto note on its own line (nothing for a blank one); OFF removes it from the notes again.
 */
export function applyAutoNote(notes, autoNote, on) {
  const current = typeof notes === 'string' ? notes : '';
  const note = typeof autoNote === 'string' ? autoNote : '';
  if (note === '') return current;
  if (on) return current === '' ? note : `${current}\n${note}`;
  return current.replace(note, '');
}
